import { Component, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SearchResult, SearchResultGrouped } from 'src/app/shared/models/SearchResult';
import { SearchService } from 'src/app/shared/services/search.service';
import { isElementInView } from '../../../functions';

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.css']
})
export class SearchComponent implements OnInit {
    @ViewChild('searchInput') public searchInput: HTMLInputElement;

    @Input() public modalRef: NgbModalRef;

    public results$: Observable<SearchResultGrouped>;
    public results: SearchResult[] = [];
    public resultsLoading = false;
    public selectedResultRow = 0;

    private _inputTimeout: any;

    constructor(
        private _searchService: SearchService,
        private _router: Router,
    ) { }

    @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent): void {
        const resultListContainer = document.getElementById('results-list');

        if (!resultListContainer) {
            return;
        }

        if (e.code === 'ArrowDown' && this.selectedResultRow < this.results.length - 1) {
            this.selectedResultRow++;
            this._scrollUpOrDown(resultListContainer);
        }

        if (e.code === 'ArrowUp' && this.selectedResultRow > 0) {
            this.selectedResultRow--;
            this._scrollUpOrDown(resultListContainer);
        }

        if (e.code === 'Enter' && this.selectedResultRow >= 0) {
            const result = this.results.find(res => res.row === this.selectedResultRow);
            this.redirectToResult(result);
        }
    }

    public ngOnInit(): void {
        this._loadResults();
    }

    public redirectToResult(clickedObject: SearchResult): void {
        switch (clickedObject?.object_type) {
            case 'Employee':
                void this._router.navigate(['/employee', clickedObject?.id, 'detail', 'overview']);
                break;
            case 'Property':
                void this._router.navigate(['/property', 'detail', clickedObject?.id]);
                break;
            case 'Document':
                void this._router.navigate(['/file-detail', 'document-template-document', clickedObject?.document_template_key, 'file', clickedObject?.file_hash, 'detail']);
                break;
            case 'Training':
                void this._router.navigate(['/training', 'overview', clickedObject?.id]);
                break;
            case 'TermWatch':
                void this._router.navigate(['/term-watch', 'overview', clickedObject?.id]);
                break;
            case 'Form':
            case 'Test':
                void this._router.navigate(['/form', 'overview', clickedObject?.id], {fragment: clickedObject?.object_type.toUpperCase()});
                break;
            case 'Chat':
                void this._router.navigate(['/chat', 'list']);
                break;
        }

        this.modalRef?.dismiss();
    }

    public onSearchChange($event: any): void {
        clearTimeout(this._inputTimeout);
        this._inputTimeout = setTimeout(() => {
            this._loadResults($event.target.value);
        }, 300);
    }

    public originalOrder = () => 0;

    public resetScroll(): void {
        this.selectedResultRow = 0;
        document.getElementById('results-list')?.scroll(0, 0);
    }

    private _scrollUpOrDown(resultListContainer: HTMLElement): void {
        const result = this.results.find(res => res.row === this.selectedResultRow);
        const resultEl = document.getElementById('result-' + result.row);
        if (!isElementInView(resultListContainer, resultEl)) {
            resultEl.scrollIntoView({block: 'nearest'});
        }
    }

    private _loadResults(term: string = null): void {
        this.selectedResultRow = 0;
        if (term && term.length >= 3) {
            this.resultsLoading = true;
            this.results$ = this._searchService.search(term)
                .pipe(
                    map((results) => {
                        const groupedResults: SearchResultGrouped = {} as SearchResultGrouped;
                        let i = 0;
                        for (const result of results) {
                            if (!groupedResults[result.object_type]) {
                                groupedResults[result.object_type] = [];
                            }
                            groupedResults[result.object_type].push(result);
                            result.row = i;
                            this.results[i] = result;
                            i++;
                        }

                        this.resultsLoading = false;
                        return groupedResults;
                    }));
        }
    }
}
